import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { MarketBanner } from '@market/react';
import {
  localizedDayJs,
  getCountryCode,
  getLanguageCode,
} from 'utilities/locales';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { FREEMAGSTRIPE, PROMOMAGSTRIPE } from 'constants/promotions';

export const APPLIED = 'applied';
export const AUTH_REQUIRED = 'authRequired';
export const ELIGIBLE = 'eligible';
export const IDV_REQUIRED = 'idvRequired';
export const REDEEMED = 'redeemed';

const STATES = new Set([
  APPLIED,
  AUTH_REQUIRED,
  ELIGIBLE,
  IDV_REQUIRED,
  REDEEMED,
]);

const DATE_FORMAT = 'MMMM Do, YYYY h:mma z';

const DEFAULT_TZ = 'America/Los_Angeles';

const buildLinks = ({
  state,
  store = getCountryCode(),
  language = getLanguageCode(),
}) => {
  if (!STATES.has(state)) return {};

  const localeCode = `${language}-${store}`;
  const langCodeParam = `lang_code=${localeCode}`;
  const loginAppParam = 'app=hardware-shop';
  const signupVariantParam = 'v=hardware-shop';
  const returnToParam = `return_to=${encodeURIComponent(
    window.location.pathname,
  )}`;

  switch (state) {
    case AUTH_REQUIRED: {
      return {
        signup: `/signup/${localeCode.toLowerCase()}?${langCodeParam}&${signupVariantParam}&${returnToParam}`,
        signin: `/login?${langCodeParam}&${loginAppParam}&${returnToParam}`,
      };
    }
    case IDV_REQUIRED: {
      return {
        signup: `/signup/${localeCode.toLowerCase()}?v=verify-your-identity`,
      };
    }
    default: {
      return {};
    }
  }
};

const getPromotionState = (name, eligible, reasons) => {
  if (eligible) {
    // TODO (@omose): Phase out FREEMAGSTRIPE
    return name === FREEMAGSTRIPE || name === PROMOMAGSTRIPE
      ? APPLIED
      : ELIGIBLE;
  }

  if (reasons.includes('limit_once_per_user')) {
    return REDEEMED;
  }

  if (reasons.includes('no_user_specified')) {
    return AUTH_REQUIRED;
  }

  if (reasons.includes('incomplete_idv')) {
    return IDV_REQUIRED;
  }

  if (
    (reasons.includes('missing_product') ||
      reasons.includes('no_applicable_products')) &&
    reasons.length === 1
  ) {
    return ELIGIBLE;
  }

  return null;
};

const FOOTNOTE = '**';

const setFooterDisclaimer = (disclaimer) => {
  if (!disclaimer || !Sq.SqShopFooter) return;

  Sq.SqShopFooter.disclaimers = [
    ...Sq.SqShopFooter.disclaimers,
    `${FOOTNOTE} ${disclaimer}`,
  ];
};

const ProductPromotionBanner = (props) => {
  const {
    name,
    eligible,
    reasons = [],
    expires_at: expiresAt,
    slug = '',
    display_verify_message: displayVerifyMessage = false,
  } = props;

  if (!name) return null;

  const state = getPromotionState(name, eligible, reasons);
  const { signin, signup } = buildLinks({ state });
  const theme = useTheme();
  const { t, i18n } = useTranslation();

  const disclaimerKey = `promotion.${name}.${state}.disclaimer`;

  let messageKey = `promotion.${name}.${state}.${slug}.message`;

  // check first for product-specific message; fallback to general promo message
  if (!i18n.exists(messageKey)) {
    messageKey = `promotion.${name}.${state}.${displayVerifyMessage && state === IDV_REQUIRED ? 'verifyMessage' : 'message'}`;
  }

  useEffect(() => {
    if (i18n.exists(disclaimerKey)) {
      let localizedExpiration;

      if (expiresAt) {
        const dayjs = localizedDayJs()
          .extend(utc)
          .extend(timezone)
          .extend(advancedFormat);

        // Attempt to display the expiration time in the user's local timezone,
        // fallback to default timezone if user timezone is invalid.
        try {
          localizedExpiration = dayjs(expiresAt).format(DATE_FORMAT);
        } catch (e) {
          if (e instanceof RangeError) {
            localizedExpiration = dayjs(expiresAt)
              .tz(DEFAULT_TZ)
              .format(DATE_FORMAT);
          } else {
            throw e;
          }
        }
      }

      setFooterDisclaimer(t(disclaimerKey, { localizedExpiration }));
    }
  }, [disclaimerKey]);

  return (
    i18n.exists(messageKey) && (
      <MarketBanner style={{ margin: theme.spacing(2, 0) }}>
        <Trans
          i18nKey={messageKey}
          values={{ footnote: FOOTNOTE }}
          components={{
            sup: <sup />,
            signup: <a href={signup} />,
            signin: <a href={signin} />,
          }}
        />
      </MarketBanner>
    )
  );
};

export default ProductPromotionBanner;
